import { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { services } from '@/mocks/services';

export default function ServiceDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    window.scrollTo(0, 0);

    const service = services.find((s) => s.slug === slug);
    if (service) {
      document.title = `${service.title} – Ganga Digital | Professional Music Services India`;

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'page-jsonld';
      script.text = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: service.title,
        description: service.description,
        url: `${import.meta.env.VITE_SITE_URL || ''}/services/${service.slug}`,
        provider: {
          '@type': 'Organization',
          name: 'Ganga Digital',
          url: import.meta.env.VITE_SITE_URL || '',
          telephone: '+919990194423',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'B-40 DSIDC Complex, Kirti Nagar',
            addressLocality: 'New Delhi',
            postalCode: '110015',
            addressCountry: 'IN',
          },
        },
        areaServed: {
          '@type': 'Country',
          name: 'India',
        },
        serviceType: service.title,
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL || ''}/` },
            { '@type': 'ListItem', position: 2, name: 'Services', item: `${import.meta.env.VITE_SITE_URL || ''}/services` },
            { '@type': 'ListItem', position: 3, name: service.title, item: `${import.meta.env.VITE_SITE_URL || ''}/services/${service.slug}` },
          ],
        },
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: `${service.title} Process`,
          itemListElement: service.process.map((step, i) => ({
            '@type': 'Offer',
            position: i + 1,
            name: step,
          })),
        },
      });
      const existing = document.getElementById('page-jsonld');
      if (existing) existing.remove();
      document.head.appendChild(script);
    }

    return () => {
      clearTimeout(t);
      const el = document.getElementById('page-jsonld');
      if (el) el.remove();
    };
  }, [slug]);

  const service = services.find((s) => s.slug === slug);

  if (!service) return <Navigate to="/services" replace />;

  const otherServices = services.filter((s) => s.slug !== slug).slice(0, 4);

  const processIcons = [
    'ri-lightbulb-line',
    'ri-draft-line',
    'ri-tools-line',
    'ri-check-double-line',
    'ri-send-plane-line',
  ];

  return (
    <div className="bg-[#0d0d0d] min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            alt={`${service.title} - Ganga Digital`}
            className="w-full h-full object-cover object-top"
            src={service.image}
          />
          <div className="absolute inset-0 bg-black/85"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-transparent"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <Link to="/services" className="inline-flex items-center gap-2 text-[#d4af37] text-sm mb-6 hover:text-white transition-colors cursor-pointer">
              <i className="ri-arrow-left-line"></i> Back to Services
            </Link>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#d4af37]/20 border border-[#d4af37]/30">
                <i className={`${service.icon} text-[#d4af37] text-2xl`}></i>
              </div>
              <div>
                <div className="text-[#d4af37] text-xs font-semibold tracking-[3px] uppercase mb-1">Our Services</div>
                <h1 className="font-heading text-4xl md:text-6xl font-bold text-white">{service.title}</h1>
              </div>
            </div>
            <p className="text-white/70 text-lg max-w-2xl mt-4">{service.shortDesc}</p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Left - Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Description */}
              <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <h2 className="font-heading text-3xl font-bold text-white mb-6">
                  About <span className="text-[#d4af37]">{service.title}</span>
                </h2>
                <p className="text-white/70 text-base leading-relaxed mb-6">{service.description}</p>
                <p className="text-white/70 text-base leading-relaxed">
                  At Ganga Digital, we bring over 30 years of music industry expertise to every project. Our {service.title.toLowerCase()} service is designed to meet the highest professional standards while remaining accessible to artists at every stage of their career. Whether you're recording your first album or producing a major label release, our team is here to support your vision.
                </p>
              </div>

              {/* Service Image */}
              <div className={`transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <div className="relative rounded-2xl overflow-hidden">
                  <img
                    alt={`${service.title} at Ganga Digital`}
                    className="w-full h-72 object-cover object-top"
                    src={service.image}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                </div>
              </div>

              {/* Process */}
              <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <h2 className="font-heading text-3xl font-bold text-white mb-8">
                  Our <span className="text-[#d4af37]">Process</span>
                </h2>
                <div className="space-y-4">
                  {service.process.map((step, i) => (
                    <div key={step} className="flex items-start gap-4 glass-card rounded-xl p-5">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#d4af37] text-black font-bold text-sm flex-shrink-0">
                        {i + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <i className={`${processIcons[i] || 'ri-check-line'} text-[#d4af37] text-lg`}></i>
                          <h3 className="text-white font-semibold font-heading">{step}</h3>
                        </div>
                        <p className="text-white/50 text-sm mt-1">
                          {i === 0 && 'We begin with a thorough consultation to understand your goals, timeline, and creative vision.'}
                          {i === 1 && 'Our team develops a detailed plan tailored to your specific requirements and budget.'}
                          {i === 2 && 'Expert execution using state-of-the-art equipment and industry-leading techniques.'}
                          {i === 3 && 'Rigorous quality checks ensure every deliverable meets our premium standards.'}
                          {i === 4 && 'Final delivery in all required formats, with ongoing support and follow-up.'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right - Sidebar */}
            <div className="space-y-6">
              {/* Benefits */}
              <div className={`glass-card rounded-2xl p-6 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <h3 className="font-heading text-xl font-bold text-white mb-5">Key Benefits</h3>
                <ul className="space-y-3">
                  {service.benefits.map((benefit) => (
                    <li key={benefit} className="flex items-center gap-3">
                      <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#d4af37]/20 flex-shrink-0">
                        <i className="ri-check-line text-[#d4af37] text-xs"></i>
                      </div>
                      <span className="text-white/80 text-sm">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA Card */}
              <div className={`rounded-2xl p-6 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ background: 'linear-gradient(135deg, #d4af37 0%, #f0d060 50%, #d4af37 100%)' }}>
                <h3 className="font-heading text-xl font-bold text-black mb-3">Start Your Project</h3>
                <p className="text-black/70 text-sm mb-5">Ready to get started? Contact us today for a free consultation.</p>
                <a
                  href={`https://wa.me/919990194423?text=${encodeURIComponent(`New Website Enquiry 🚀\n\nService: ${service.title}\nMessage: I would like to know more about your ${service.title} service.`)}`}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-full text-sm font-semibold text-center text-white whitespace-nowrap bg-green-600 hover:bg-green-700 transition-all cursor-pointer"
                >
                  <i className="ri-whatsapp-line"></i> Enquire on WhatsApp
                </a>
                <Link to="/contact" className="block w-full py-3 rounded-full text-sm font-semibold text-center text-[#d4af37] whitespace-nowrap bg-black hover:bg-black/80 transition-all cursor-pointer mt-2">
                  Get Free Consultation
                </Link>
                <a href="tel:09990194423" className="flex items-center justify-center gap-2 mt-3 text-black/80 text-sm hover:text-black transition-colors cursor-pointer">
                  <i className="ri-phone-line"></i> 09990194423
                </a>
              </div>

              {/* Stats */}
              <div className={`glass-card rounded-2xl p-6 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <h3 className="font-heading text-lg font-bold text-white mb-4">Why Ganga Digital?</h3>
                <div className="space-y-4">
                  {[
                    { icon: 'ri-building-line', value: '30+', label: 'Years Experience' },
                    { icon: 'ri-team-line', value: '70+', label: 'Team Members' },
                    { icon: 'ri-music-2-line', value: '10K+', label: 'Songs Produced' },
                    { icon: 'ri-award-line', value: '50+', label: 'Industry Awards' },
                  ].map((stat) => (
                    <div key={stat.label} className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#d4af37]/10 flex-shrink-0">
                        <i className={`${stat.icon} text-[#d4af37] text-base`}></i>
                      </div>
                      <div>
                        <div className="text-[#d4af37] font-bold font-heading text-lg leading-none">{stat.value}</div>
                        <div className="text-white/50 text-xs">{stat.label}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-16 bg-[#080808]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-3xl font-bold text-white mb-8">
            Other <span className="text-[#d4af37]">Services</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {otherServices.map((svc) => (
              <Link
                key={svc.slug}
                to={`/services/${svc.slug}`}
                className="group glass-card rounded-xl overflow-hidden cursor-pointer transition-all hover:border-[#d4af37]/40 hover:-translate-y-1"
              >
                <div className="h-32 overflow-hidden relative">
                  <img alt={svc.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" src={svc.image} />
                  <div className="absolute inset-0 bg-black/60"></div>
                  <div className="absolute top-3 left-3 w-8 h-8 flex items-center justify-center rounded-lg bg-[#d4af37]/20">
                    <i className={`${svc.icon} text-[#d4af37] text-sm`}></i>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-white font-semibold text-sm group-hover:text-[#d4af37] transition-colors">{svc.title}</h3>
                  <div className="flex items-center gap-1 text-[#d4af37] text-xs mt-2">
                    Learn More <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/services" className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-[#d4af37] whitespace-nowrap border border-[#d4af37]/40 hover:border-[#d4af37] hover:bg-[#d4af37]/10 transition-all cursor-pointer">
              View All Services <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
